import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ChatCircleText, EyeSlash, ArrowsClockwise, Briefcase, User,
} from "@phosphor-icons/react";

/**
 * v3.2.59 · Unified message inbox.
 *
 * PE — one thread per job (their only counterparty is the client owner).
 * Client — one thread per (job × PE) so each engineer conversation gets
 * its own row. Clicking a thread deep-links to the job's message pane.
 */
export default function InboxPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [busy, setBusy]       = useState(false);
  const [role, setRole]       = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/inbox");
      setRole(data.role);
      setThreads(data.threads || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load inbox");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const openThread = async (t) => {
    try { await api.post("/inbox/mark-read", { thread_key: t.key }); }
    catch { /* ignore */ }
    nav(`/jobs/${t.job_id}`);
  };

  // Group threads by job so the client view renders as
  // Job → { PE row, PE row, PE row }.
  const grouped = useMemo(() => {
    const g = new Map();
    for (const t of threads) {
      if (!g.has(t.job_id)) g.set(t.job_id, { job_title: t.job_title, job_status: t.job_status, rows: [] });
      g.get(t.job_id).rows.push(t);
    }
    return Array.from(g.entries()).map(([job_id, v]) => ({ job_id, ...v }));
  }, [threads]);

  const totalUnread = useMemo(
    () => threads.reduce((n, t) => n + (t.unread_count || 0), 0),
    [threads]
  );

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-tag">// inbox</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter"
                 data-testid="inbox-heading">
              Messages
            </h1>
            <p className="mt-2 text-sm text-gray-400 max-w-lg leading-relaxed">
              {role === "pe"
                ? "Every job you're chatting on, grouped by job. Click a row to open the thread."
                : role === "client"
                  ? "Every engineer chatting with you, grouped by job and then by PE. Click a row to open that thread."
                  : "Your message inbox."}
            </p>
          </div>
          <button onClick={load} disabled={busy}
                   className="text-xs font-mono uppercase tracking-wider text-gray-500 hover:text-white inline-flex items-center gap-1"
                   data-testid="inbox-refresh">
            <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="mt-4 text-xs font-mono text-gray-500">
          {threads.length} thread{threads.length === 1 ? "" : "s"}
          {totalUnread > 0 && (
            <span className="ml-2 text-[#FFCC00]">· {totalUnread} unread</span>
          )}
        </div>

        {threads.length === 0 && !busy && (
          <div className="mt-8 border border-white/10 bg-white/[0.03] p-10 text-center text-sm font-mono text-gray-400"
                data-testid="inbox-empty">
            <ChatCircleText size={30} weight="duotone" className="mx-auto mb-3 text-gray-600" />
            No messages yet.
            {role === "pe" && (
              <div className="mt-1 text-xs text-gray-500">
                Bid on an open job — the moment you submit a proposal, the client can message you here.
              </div>
            )}
            {role === "client" && (
              <div className="mt-1 text-xs text-gray-500">
                Post a job — engineers who submit proposals will show up here for you to chat with.
              </div>
            )}
          </div>
        )}

        <div className="mt-8 space-y-6" data-testid="inbox-list">
          {grouped.map((g) => (
            <div key={g.job_id} className="border border-white/10 bg-black/40" data-testid={`inbox-job-${g.job_id}`}>
              <Link to={`/jobs/${g.job_id}`}
                     className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition">
                <div className="flex items-center gap-2 min-w-0">
                  <Briefcase size={14} weight="fill" className="text-[#00E5FF] shrink-0" />
                  <div className="font-display text-base font-black text-white truncate">{g.job_title}</div>
                  <span className="text-[10px] font-mono uppercase text-gray-500 border border-white/10 px-1.5 py-0.5 flex-shrink-0">
                    {g.job_status}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-gray-500 shrink-0">
                  {g.rows.length} thread{g.rows.length === 1 ? "" : "s"}
                </span>
              </Link>
              <div>
                {g.rows.map((t) => {
                  const isUnread = t.unread_count > 0;
                  return (
                    <button key={t.key}
                      onClick={() => openThread(t)}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/[0.04] transition flex items-start gap-3 ${isUnread ? "bg-[#00E5FF]/[0.03]" : ""}`}
                      data-testid={`inbox-thread-${t.key}`}>
                      <div className="mt-0.5 w-8 h-8 border border-white/10 bg-black/60 flex items-center justify-center flex-shrink-0">
                        {t.counterparty_incognito
                          ? <EyeSlash size={14} weight="fill" className="text-[#00E5FF]" />
                          : <User size={14} weight="bold" className="text-gray-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-mono text-sm truncate ${isUnread ? "text-white font-bold" : "text-gray-300"}`}>
                            {t.counterparty_name}
                          </span>
                          {t.counterparty_incognito && (
                            <span className="text-[9px] font-mono uppercase tracking-widest text-[#00E5FF] border border-[#00E5FF]/30 px-1">
                              Incognito
                            </span>
                          )}
                          {isUnread && (
                            <span className="text-[9px] font-mono uppercase tracking-widest text-black bg-[#FFCC00] px-1.5">
                              {t.unread_count} new
                            </span>
                          )}
                          <span className="ml-auto text-[10px] font-mono text-gray-500 shrink-0">
                            {t.last_message_at ? new Date(t.last_message_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                          </span>
                        </div>
                        <div className={`mt-1 text-xs truncate ${isUnread ? "text-gray-200" : "text-gray-500"}`}>
                          {t.last_sender_id === (user?.id || "") ? <span className="text-gray-500">You: </span> : null}
                          {t.last_message_preview || "—"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
